import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  X, ChevronLeft, ChevronRight, Volume2, VolumeX, Pause, Play,
  Eye, Send, ChevronUp, MessageCircle, MoreVertical, Trash2, Plus, Bookmark
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

import { getMediaUrl } from '@/utils/mediaUrl';

export default function StoryViewer({ 
  userStories, // Array of UserStoriesResponse
  initialUserIndex = 0,
  currentUserId, // Current logged-in user's ID
  onClose,
  onStoryView,
  onStoryDeleted, // Callback when story is deleted
  onAddAnother, // Callback to open create modal
  onSaveToHighlight // Callback to save to highlight
}) {
  const [currentUserIndex, setCurrentUserIndex] = useState(initialUserIndex);
  const [currentStoryIndex, setCurrentStoryIndex] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  // Viewers panel
  const [showViewers, setShowViewers] = useState(false);
  const [viewers, setViewers] = useState([]);
  const [viewersLoading, setViewersLoading] = useState(false);
  
  // Reply input
  const [replyText, setReplyText] = useState('');
  const [sendingReply, setSendingReply] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(false);
  
  // Owner menu
  const [showOwnerMenu, setShowOwnerMenu] = useState(false);
  const [deleting, setDeleting] = useState(false);
  
  const videoRef = useRef(null);
  const progressInterval = useRef(null);
  const containerRef = useRef(null);
  const replyInputRef = useRef(null);

  const currentUser = userStories[currentUserIndex];
  const currentStory = currentUser?.stories[currentStoryIndex];
  const totalUsers = userStories.length;
  const totalStoriesForUser = currentUser?.stories.length || 0;
  
  // Check if current user owns this story
  const isOwner = currentUser?.user_id === currentUserId;

  // Mark story as viewed
  useEffect(() => {
    if (currentStory && onStoryView) {
      onStoryView(currentStory.id);
    }
  }, [currentStory?.id]);

  // Fetch viewers when showing viewers panel
  useEffect(() => {
    if (showViewers && currentStory && isOwner) {
      fetchViewers();
    }
  }, [showViewers, currentStory?.id]);

  const fetchViewers = async () => {
    if (!currentStory) return;
    
    setViewersLoading(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      const response = await axios.get(`${API}/stories/${currentStory.id}/viewers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setViewers(response.data);
    } catch (error) {
      console.error('Failed to fetch viewers:', error);
    } finally {
      setViewersLoading(false);
    }
  };

  // Handle progress timer
  useEffect(() => {
    if (!currentStory || isPaused || isLoading || showViewers || showReplyInput || showOwnerMenu) {
      return;
    }

    const duration = currentStory.duration * 1000;
    const interval = 50;
    const increment = (interval / duration) * 100;

    progressInterval.current = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval.current);
          goToNextStory();
          return 0;
        }
        return prev + increment;
      });
    }, interval);

    return () => {
      if (progressInterval.current) {
        clearInterval(progressInterval.current);
      }
    };
  }, [currentStory?.id, isPaused, isLoading, showViewers, showReplyInput, showOwnerMenu]);

  // Handle video playback
  useEffect(() => {
    if (currentStory?.media_type === 'video' && videoRef.current) {
      if (isPaused || showViewers || showReplyInput || showOwnerMenu) {
        videoRef.current.pause();
      } else {
        videoRef.current.play().catch(() => {});
      }
    }
  }, [isPaused, currentStory?.id, showViewers, showReplyInput, showOwnerMenu]);

  // Reset state when story changes
  useEffect(() => {
    setProgress(0);
    setIsLoading(true);
    setShowViewers(false);
    setShowReplyInput(false);
    setShowOwnerMenu(false);
    setReplyText('');
  }, [currentStory?.id]);

  const goToNextStory = useCallback(() => {
    if (currentStoryIndex < totalStoriesForUser - 1) {
      setCurrentStoryIndex((prev) => prev + 1);
    } else if (currentUserIndex < totalUsers - 1) {
      setCurrentUserIndex((prev) => prev + 1);
      setCurrentStoryIndex(0);
    } else {
      onClose();
    }
  }, [currentStoryIndex, totalStoriesForUser, currentUserIndex, totalUsers, onClose]);

  const goToPrevStory = useCallback(() => {
    if (currentStoryIndex > 0) {
      setCurrentStoryIndex((prev) => prev - 1);
    } else if (currentUserIndex > 0) {
      const prevUserIndex = currentUserIndex - 1;
      const prevUserStories = userStories[prevUserIndex]?.stories.length || 1;
      setCurrentUserIndex(prevUserIndex);
      setCurrentStoryIndex(prevUserStories - 1);
    }
  }, [currentStoryIndex, currentUserIndex, userStories]);

  const handleContainerClick = (e) => {
    if (showViewers || showReplyInput || showOwnerMenu) return;
    
    const rect = containerRef.current?.getBoundingClientRect();
    if (!rect) return;
    
    const clickX = e.clientX - rect.left;
    const width = rect.width;
    
    if (clickX < width / 3) {
      goToPrevStory();
    } else if (clickX > (width * 2) / 3) {
      goToNextStory();
    } else {
      setIsPaused((prev) => !prev);
    }
  };

  const handleKeyDown = useCallback((e) => {
    if (showReplyInput) return;
    
    switch (e.key) {
      case 'ArrowLeft':
        goToPrevStory();
        break;
      case 'ArrowRight':
        goToNextStory();
        break;
      case ' ':
        e.preventDefault();
        setIsPaused((prev) => !prev);
        break;
      case 'Escape':
        if (showOwnerMenu) {
          setShowOwnerMenu(false);
        } else if (showViewers) {
          setShowViewers(false);
        } else {
          onClose();
        }
        break;
      default:
        break;
    }
  }, [goToPrevStory, goToNextStory, onClose, showViewers, showReplyInput, showOwnerMenu]);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !currentStory) return;
    
    setSendingReply(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.post(`${API}/stories/${currentStory.id}/reply`, {
        message: replyText.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Reply sent!');
      setReplyText('');
      setShowReplyInput(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    } finally {
      setSendingReply(false);
    }
  };

  const handleDeleteStory = async () => {
    if (!currentStory) return;
    
    setDeleting(true);
    try {
      const token = localStorage.getItem('dharmbook_token');
      await axios.delete(`${API}/stories/${currentStory.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Darshan deleted');
      setShowOwnerMenu(false);
      
      // Handle navigation after delete
      if (totalStoriesForUser <= 1) {
        // Last story for this user, close viewer
        if (onStoryDeleted) onStoryDeleted(currentStory.id);
        onClose();
      } else if (currentStoryIndex >= totalStoriesForUser - 1) {
        // Deleted last story, go to previous
        setCurrentStoryIndex((prev) => prev - 1);
        if (onStoryDeleted) onStoryDeleted(currentStory.id);
      } else {
        // Stay at same index, next story will shift into place
        if (onStoryDeleted) onStoryDeleted(currentStory.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete darshan');
    } finally {
      setDeleting(false);
    }
  };

  const handleAddAnother = () => {
    setShowOwnerMenu(false);
    onClose();
    if (onAddAnother) onAddAnother();
  };

  const handleSaveToHighlight = () => {
    setShowOwnerMenu(false);
    if (onSaveToHighlight && currentStory) {
      onSaveToHighlight(currentStory);
    }
  };

  if (!currentStory) {
    return null;
  }

  const mediaUrl = getMediaUrl(currentStory.media_url);

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black flex items-center justify-center"
      data-testid="story-viewer"
    >
      {/* Story Container */}
      <div 
        ref={containerRef}
        className="relative w-full h-full max-w-md mx-auto flex flex-col"
        onClick={handleContainerClick}
      >
        {/* Progress Bars */}
        <div className="absolute top-0 left-0 right-0 z-20 flex gap-1 p-2 pt-3">
          {currentUser?.stories.map((story, idx) => (
            <div
              key={story.id}
              className="flex-1 h-1 bg-white/30 rounded-full overflow-hidden"
            >
              <div
                className="h-full bg-white rounded-full transition-all duration-100"
                style={{
                  width: idx < currentStoryIndex 
                    ? '100%' 
                    : idx === currentStoryIndex 
                      ? `${progress}%` 
                      : '0%'
                }}
              />
            </div>
          ))}
        </div>

        {/* Header */}
        <div className="absolute top-6 left-0 right-0 z-20 flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <img
              src={currentUser?.user_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(currentUser?.user_name || 'User')}&background=FF9933&color=fff`}
              alt={currentUser?.user_name}
              className="w-10 h-10 rounded-full border-2 border-white object-cover"
            />
            <div>
              <p className="text-white font-semibold text-sm">{currentUser?.user_name}</p>
              <p className="text-white/70 text-xs">
                {new Date(currentStory.created_at).toLocaleTimeString([], { 
                  hour: '2-digit', 
                  minute: '2-digit' 
                })}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-1">
            {/* Pause/Play */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsPaused((prev) => !prev);
              }}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              data-testid="story-pause-btn"
            >
              {isPaused ? (
                <Play className="w-5 h-5 text-white" fill="white" />
              ) : (
                <Pause className="w-5 h-5 text-white" fill="white" />
              )}
            </button>
            
            {/* Mute (for videos) */}
            {currentStory.media_type === 'video' && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsMuted((prev) => !prev);
                  if (videoRef.current) {
                    videoRef.current.muted = !isMuted;
                  }
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                data-testid="story-mute-btn"
              >
                {isMuted ? (
                  <VolumeX className="w-5 h-5 text-white" />
                ) : (
                  <Volume2 className="w-5 h-5 text-white" />
                )}
              </button>
            )}
            
            {/* 3-Dot Menu (Owner only) */}
            {isOwner && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowOwnerMenu(true);
                  setIsPaused(true);
                }}
                className="p-2 hover:bg-white/20 rounded-full transition-colors"
                data-testid="story-menu-btn"
              >
                <MoreVertical className="w-5 h-5 text-white" />
              </button>
            )}
            
            {/* Close */}
            <button
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              className="p-2 hover:bg-white/20 rounded-full transition-colors"
              data-testid="story-close-btn"
            >
              <X className="w-6 h-6 text-white" />
            </button>
          </div>
        </div>

        {/* Story Content */}
        <div className="flex-1 flex items-center justify-center">
          {/* Loading Spinner */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-10">
              <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
            </div>
          )}
          
          {currentStory.media_type === 'image' ? (
            <img
              src={mediaUrl}
              alt="Darshan"
              className="max-w-full max-h-full object-contain"
              onLoad={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
            />
          ) : (
            <video
              ref={videoRef}
              src={mediaUrl}
              className="max-w-full max-h-full object-contain"
              autoPlay
              playsInline
              muted={isMuted}
              loop={false}
              onLoadedData={() => setIsLoading(false)}
              onError={() => setIsLoading(false)}
              onEnded={goToNextStory}
            />
          )}
        </div>

        {/* Bottom Actions */}
        <div className="absolute bottom-0 left-0 right-0 z-20 p-4">
          {/* Reply Input (Non-owner) */}
          {!isOwner && showReplyInput && (
            <div 
              className="flex items-center gap-2 bg-white/10 backdrop-blur-md rounded-full p-2 mb-3"
              onClick={(e) => e.stopPropagation()}
            >
              <input
                ref={replyInputRef}
                type="text"
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Reply to darshan..."
                className="flex-1 bg-transparent text-white placeholder-white/50 px-3 py-2 outline-none text-sm"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSendReply();
                }}
                autoFocus
              />
              <button
                onClick={handleSendReply}
                disabled={sendingReply || !replyText.trim()}
                className="p-2 bg-[#FF9933] rounded-full disabled:opacity-50"
              >
                <Send className="w-5 h-5 text-white" />
              </button>
            </div>
          )}
          
          <div className="flex items-center justify-between">
            {/* Viewers Button (Owner only) */}
            {isOwner ? (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowViewers(true);
                  setIsPaused(true);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white text-sm hover:bg-white/20 transition-colors"
                data-testid="story-viewers-btn"
              >
                <Eye className="w-4 h-4" />
                <span>{currentStory.views_count} views</span>
                <ChevronUp className="w-4 h-4" />
              </button>
            ) : (
              /* Reply Button (Non-owner) */
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowReplyInput(true);
                  setIsPaused(true);
                  setTimeout(() => replyInputRef.current?.focus(), 100);
                }}
                className="flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-md rounded-full text-white text-sm hover:bg-white/20 transition-colors"
                data-testid="story-reply-btn"
              >
                <MessageCircle className="w-4 h-4" />
                <span>Reply</span>
              </button>
            )}
            
            {/* Navigation dots for desktop */}
            <div className="hidden md:flex items-center gap-1">
              {userStories.map((user, idx) => (
                <button
                  key={user.user_id}
                  onClick={(e) => {
                    e.stopPropagation();
                    setCurrentUserIndex(idx);
                    setCurrentStoryIndex(0);
                  }}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    idx === currentUserIndex ? 'bg-white' : 'bg-white/40'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>

        {/* Navigation Arrows (Desktop) */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToPrevStory();
          }}
          className="hidden md:flex absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors"
          data-testid="story-prev-btn"
        >
          <ChevronLeft className="w-6 h-6 text-white" />
        </button>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            goToNextStory();
          }}
          className="hidden md:flex absolute right-2 top-1/2 -translate-y-1/2 w-10 h-10 items-center justify-center bg-white/20 hover:bg-white/30 rounded-full transition-colors"
          data-testid="story-next-btn"
        >
          <ChevronRight className="w-6 h-6 text-white" />
        </button>
      </div>

      {/* Owner Menu (3-dot menu options) */}
      {showOwnerMenu && (
        <div 
          className="fixed inset-0 z-[110] flex items-end justify-center"
          onClick={() => {
            setShowOwnerMenu(false);
            setIsPaused(false);
          }}
        >
          <div 
            className="w-full max-w-md bg-[#1a1a1a] rounded-t-3xl overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
            data-testid="story-owner-menu"
          >
            {/* Menu Items */}
            <div className="py-2">
              {/* Delete Story */}
              <button
                onClick={handleDeleteStory}
                disabled={deleting}
                className="w-full flex items-center gap-4 px-6 py-4 hover:bg-white/5 transition-colors text-left"
                data-testid="delete-story-btn"
              >
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center">
                  <Trash2 className="w-5 h-5 text-red-500" />
                </div>
                <div>
                  <p className="text-white font-medium">
                    {deleting ? 'Deleting...' : 'Delete Darshan'}
                  </p>
                  <p className="text-white/50 text-xs">This cannot be undone</p>
                </div>
              </button>
              
              {/* Add Another */}
              <button
                onClick={handleAddAnother}
                className="w-full flex items-center gap-4 px-6 py-4 hover:bg-white/5 transition-colors text-left"
                data-testid="add-another-story-btn"
              >
                <div className="w-10 h-10 rounded-full bg-[#FF9933]/20 flex items-center justify-center">
                  <Plus className="w-5 h-5 text-[#FF9933]" />
                </div>
                <div>
                  <p className="text-white font-medium">Add Another</p>
                  <p className="text-white/50 text-xs">Share more moments</p>
                </div>
              </button>
              
              {/* Save to Highlight */}
              <button
                onClick={handleSaveToHighlight}
                className="w-full flex items-center gap-4 px-6 py-4 hover:bg-white/5 transition-colors text-left"
                data-testid="save-highlight-btn"
              >
                <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Bookmark className="w-5 h-5 text-purple-500" />
                </div>
                <div>
                  <p className="text-white font-medium">Save to Highlight</p>
                  <p className="text-white/50 text-xs">Keep this darshan on your profile</p>
                </div>
              </button>
            </div>
            
            {/* Cancel Button */}
            <div className="border-t border-white/10 p-4">
              <button
                onClick={() => {
                  setShowOwnerMenu(false);
                  setIsPaused(false);
                }}
                className="w-full py-3 bg-white/10 hover:bg-white/15 rounded-xl text-white font-medium transition-colors"
                data-testid="cancel-menu-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Viewers Panel (Slide up from bottom) */}
      {showViewers && (
        <div 
          className="fixed inset-0 z-[110] flex items-end justify-center"
          onClick={() => {
            setShowViewers(false);
            setIsPaused(false);
          }}
        >
          <div 
            className="w-full max-w-md bg-white rounded-t-3xl max-h-[70vh] overflow-hidden animate-slide-up"
            onClick={(e) => e.stopPropagation()}
            data-testid="story-viewers-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">
                Viewed by {currentStory.views_count}
              </h3>
              <button
                onClick={() => {
                  setShowViewers(false);
                  setIsPaused(false);
                }}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            {/* Viewers List */}
            <div className="overflow-y-auto max-h-[50vh] p-2">
              {viewersLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="w-8 h-8 border-3 border-[#FF9933]/30 border-t-[#FF9933] rounded-full animate-spin" />
                </div>
              ) : viewers.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Eye className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                  <p>No viewers yet</p>
                </div>
              ) : (
                <div className="space-y-1">
                  {viewers.map((viewer) => (
                    <div
                      key={viewer.viewer_id}
                      className="flex items-center gap-3 p-3 rounded-xl hover:bg-gray-50 transition-colors"
                    >
                      <img
                        src={viewer.viewer_avatar || `https://ui-avatars.com/api/?name=${encodeURIComponent(viewer.viewer_name)}&background=FF9933&color=fff`}
                        alt={viewer.viewer_name}
                        className="w-11 h-11 rounded-full object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{viewer.viewer_name}</p>
                        <p className="text-xs text-gray-500">
                          {new Date(viewer.viewed_at).toLocaleString([], {
                            month: 'short',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Animation styles */}
      <style>{`
        @keyframes slide-up {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}
